import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Grid,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { transactionsApi, TransactionReportRow } from '../services/transactions';

export const REPORT_COLUMN_DEFS: { id: keyof TransactionReportRow; label: string }[] = [
  { id: 'modification_date', label: 'Modification date' },
  { id: 'created_date', label: 'Created date' },
  { id: 'company_name', label: 'Company name' },
  { id: 'transaction_number', label: 'Transaction number' },
  { id: 'service_name', label: 'Service name' },
  { id: 'payment_amount', label: 'Payment amount' },
  { id: 'unit_price', label: 'Unit price' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'discount', label: 'Discount' },
  { id: 'payment_comment', label: 'Comment / payment' },
  { id: 'manager_name', label: 'Company manager' },
  { id: 'status', label: 'Transaction status' },
];

const DEFAULT_COLUMN_IDS: string[] = REPORT_COLUMN_DEFS.slice(0, 7).map((c) => c.id as string);

function formatReportDecimal(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

function cellValue(row: TransactionReportRow, colId: keyof TransactionReportRow): string {
  const v = row[colId];
  if (colId === 'unit_price') {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number' && !Number.isNaN(v)) return formatReportDecimal(v);
    return '—';
  }
  if (v === null || v === undefined) {
    if (colId === 'modification_date' || colId === 'created_date') return '';
    return '';
  }
  if (
    (colId === 'modification_date' || colId === 'created_date') &&
    typeof v === 'string'
  ) {
    try {
      return format(new Date(v), 'yyyy-MM-dd HH:mm');
    } catch {
      return v;
    }
  }
  if (colId === 'payment_amount' || colId === 'quantity' || colId === 'discount') {
    const n = typeof v === 'number' ? v : parseFloat(String(v)) || 0;
    return formatReportDecimal(n);
  }
  return String(v);
}

function escapeCsvCell(s: string): string {
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Column width in Excel character units (wch); fits header labels and cell content. */
function xlsxColumnWidths(
  cols: { label: string }[],
  objects: Record<string, string>[]
): { wch: number }[] {
  const padding = 2;
  const minW = 10;
  const maxW = 72;
  return cols.map((c) => {
    let maxLen = c.label.length;
    for (const obj of objects) {
      const len = (obj[c.label] ?? '').length;
      if (len > maxLen) maxLen = len;
    }
    return { wch: Math.min(maxW, Math.max(minW, maxLen + padding)) };
  });
}

interface TransactionReportDialogProps {
  open: boolean;
  onClose: () => void;
}

export const TransactionReportDialog: React.FC<TransactionReportDialogProps> = ({ open, onClose }) => {
  const [selected, setSelected] = useState<string[]>([...DEFAULT_COLUMN_IDS]);
  const [fileFormat, setFileFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleColumnChange = (value: string[]) => {
    const order = REPORT_COLUMN_DEFS.map((c) => c.id);
    setSelected(
      value.sort((a, b) => order.indexOf(a as keyof TransactionReportRow) - order.indexOf(b as keyof TransactionReportRow))
    );
  };

  const selectAll = () => setSelected(REPORT_COLUMN_DEFS.map((c) => c.id as string));
  const selectNone = () => setSelected([]);

  const handleDownload = async () => {
    if (selected.length === 0) {
      setError('Select at least one column');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const includeService = selected.includes('service_name');
      const { rows } = await transactionsApi.getReport({
        expandLines: includeService,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        updatedFrom: updatedFrom || undefined,
        updatedTo: updatedTo || undefined,
      });
      if (!rows.length) {
        setError('No data for the selected filters');
        setLoading(false);
        return;
      }
      const cols = REPORT_COLUMN_DEFS.filter((c) => selected.includes(c.id as string));
      const objects = rows.map((row) => {
        const o: Record<string, string> = {};
        for (const c of cols) {
          o[c.label] = cellValue(row, c.id);
        }
        return o;
      });
      const stamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
      if (fileFormat === 'csv') {
        const header = cols.map((c) => c.label);
        const lines = [
          header.map(escapeCsvCell).join(','),
          ...objects.map((obj) =>
            header.map((h) => escapeCsvCell(obj[h] ?? '')).join(',')
          ),
        ];
        const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `transactions_report_${stamp}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      } else {
        const ws = XLSX.utils.json_to_sheet(objects, { header: cols.map((c) => c.label) });
        ws['!cols'] = xlsxColumnWidths(cols, objects);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, `transactions_report_${stamp}.xlsx`);
      }
      onClose();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Transaction report</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Filters (optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Created from"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Created to"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Last modified from"
                value={updatedFrom}
                onChange={(e) => setUpdatedFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Uses updated date, or created date if never updated"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Last modified to"
                value={updatedTo}
                onChange={(e) => setUpdatedTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              If the &quot;Service name&quot; column is not selected, each invoice is one row: payment amount is the
              transaction total, quantity is the sum of line quantities, unit price is &quot;—&quot;, discount is the
              sum of line discounts.
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <FormControl sx={{ minWidth: 360, flex: 1 }} size="small">
                <InputLabel>Columns</InputLabel>
                <Select
                  multiple
                  value={selected}
                  onChange={(e) => handleColumnChange(e.target.value as string[])}
                  input={<OutlinedInput label="Columns" />}
                  renderValue={() => `${selected.length} / ${REPORT_COLUMN_DEFS.length} selected`}
                >
                  {REPORT_COLUMN_DEFS.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={selected.indexOf(c.id) > -1} size="small" />
                      <ListItemText primary={c.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" variant="outlined" onClick={selectAll}>
                Select all
              </Button>
              <Button size="small" variant="outlined" onClick={selectNone}>
                Clear selection
              </Button>
            </Box>
          </Box>
          <FormControl>
            <FormLabel>Format</FormLabel>
            <RadioGroup row value={fileFormat} onChange={(_, v) => setFileFormat(v as 'csv' | 'xlsx')}>
              <FormControlLabel value="xlsx" control={<Radio size="small" />} label="XLSX" />
              <FormControlLabel value="csv" control={<Radio size="small" />} label="CSV" />
            </RadioGroup>
          </FormControl>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleDownload} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : undefined}>
          {loading ? 'Building…' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
